/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { TooltipLayer } from '@/components/TooltipLayer';
import { GlobalProgress, PageLoading } from '@/components/loading';
import { RequireWorkspace } from '@/auth/RequireWorkspace';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/design-system';
import { useUiStore } from '@/stores/uiStore';

import { TourInvite } from '@/features/tour/TourInvite';
import { TourOverlay } from '@/features/tour/TourOverlay';
import { Rail } from './shell/Rail';
import { RAIL_DOCK_QUERY, useRail } from './shell/useRail';
import { Topbar } from './shell/Topbar';
import { FeedbackFab } from './shell/FeedbackFab';
import { ResourceLinksModal } from './shell/ResourceLinksModal';
import { rememberedBase } from './shell/nav';
import { useTheme } from './shell/useTheme';
import { useMetricsTracking } from './shell/useMetricsTracking';
import { useSyncUserSettings } from './shell/useSyncUserSettings';
import './shell/shell.css';
import './shell/modals.css';

/** Below this width the rail is the phone drawer (shell.css). */
const DRAWER_QUERY = '(max-width: 599.98px)';

/** Suspense fallback while a route chunk loads: the page skeleton, not a blank flash. */
export function RouteLoading() {
  return (
    <div className="page" aria-busy="true">
      <PageLoading />
    </div>
  );
}

/** Manufacturing application chrome: icon rail + topbar + routed content. */
export function AppShell() {
  useTheme();
  useMetricsTracking();
  useSyncUserSettings();
  const pickerOpen = useUiStore((s) => s.pickerOpen);
  const resourcesOpen = useUiStore((s) => s.resourcesOpen);
  const closeResources = useUiStore((s) => s.closeResources);
  const rememberRoute = useUiStore((s) => s.rememberRoute);
  const tourOpen = useUiStore((s) => s.tourOpen);
  const setRailPeek = useUiStore((s) => s.setRailPeek);
  const rail = useRail();
  const drawer = useMediaQuery(DRAWER_QUERY);
  const docked = useMediaQuery(RAIL_DOCK_QUERY);
  const { pathname, search } = useLocation();

  // A rail opened over the page closes on navigation, and once the width moves into the phone
  // drawer or the desktop dock: neither has room for it, and coming back does not reopen it.
  useEffect(() => {
    setRailPeek(false);
  }, [pathname, drawer, docked, setRailPeek]);

  // The phone nav drawer (below 600px the rail is off-canvas). Any navigation closes it, the
  // browser's back button included.
  const [navOpen, setNavOpen] = useState(false);
  const [navPath, setNavPath] = useState(pathname);
  if (navPath !== pathname) {
    setNavPath(pathname);
    setNavOpen(false);
  }
  const closeNav = useCallback(() => setNavOpen(false), []);
  const toggleNav = useCallback(() => setNavOpen((open) => !open), []);

  // Remember URL for query-driven tools so the rail can restore them.
  useEffect(() => {
    const base = rememberedBase(pathname);
    if (base) rememberRoute(base, `${pathname}${search}`);
  }, [pathname, search, rememberRoute]);

  // Tours are opt-in: a one-time invitation, a page's Take a tour button or Help > Quick tour.
  // Auto-starting one put a full-screen click shield over the icon rail, so Home / Repair
  // overview / MECL looked dead until the user found Skip - not acceptable on the plant floor.

  return (
    <div
      className={cn(
        'ewd',
        pickerOpen && 'vp-open',
        navOpen && 'nav-open',
        rail.open && 'rail-open',
        rail.over && 'rail-over',
      )}
    >
      <GlobalProgress />
      <Rail open={navOpen} onClose={closeNav} />
      <div className="ewd-main">
        <Topbar navOpen={navOpen} onToggleNav={toggleNav} />
        <main className="ewd-home" id="main">
          <Suspense fallback={<RouteLoading />}>
            <RequireWorkspace>
              <Outlet />
            </RequireWorkspace>
          </Suspense>
        </main>
      </div>
      <FeedbackFab />
      {resourcesOpen && <ResourceLinksModal onClose={closeResources} />}
      <TooltipLayer />
      <TourInvite />
      {tourOpen && <TourOverlay />}
    </div>
  );
}
